import { useLayoutEffect } from "react";

// A short stability window catches template effects that update the DOM after commit.
// Fonts, pictures, backgrounds and hyphenation have separate completion checks.
export function observePrintReadiness(onReady, root = document.body) {
  let active = true;
  let timer;
  let lastChange = performance.now();
  let previousLayout;
  let revision = 0;
  const backgroundImages = new Map();
  const observer = new MutationObserver(() => {
    lastChange = performance.now();
    revision += 1;
  });
  observer.observe(root, { subtree: true, childList: true, attributes: true, characterData: true });

  async function check() {
    if (!active) return;
    const currentRevision = revision;
    const profiles = [...root.querySelectorAll(".Profile")];
    if (profiles.length) {
      // Force layout before awaiting fonts; this starts any fonts used by the template.
      const layout = profiles
        .map((profile) => {
          const rect = profile.getBoundingClientRect();
          return [rect.x, rect.y, rect.width, rect.height, profile.scrollHeight].join(",");
        })
        .join(";");
      if (layout !== previousLayout) {
        previousLayout = layout;
        lastChange = performance.now();
      }
      const fontsWereLoading = document.fonts.status !== "loaded";
      await document.fonts.ready;
      if (fontsWereLoading) lastChange = performance.now();
      const elements = [...root.querySelectorAll("*")];
      for (const element of elements) {
        for (const pseudo of [null, "::before", "::after"]) {
          const background = window.getComputedStyle(element, pseudo).backgroundImage;
          for (const match of background.matchAll(/url\((?:"([^"]*)"|'([^']*)'|([^)]*))\)/g)) {
            const url = match[1] ?? match[2] ?? match[3].trim();
            if (!backgroundImages.has(url)) {
              const image = new Image();
              image.src = url;
              backgroundImages.set(url, image);
            }
          }
        }
      }
      const images = [...root.querySelectorAll("img"), ...backgroundImages.values()];
      // A failed image is settled too: preserve the existing missing-image behavior.
      if (
        images.every((image) => image.complete) &&
        document.fonts.status === "loaded" &&
        window.getComputedStyle(document.documentElement).visibility !== "hidden"
      ) {
        await Promise.all(
          images.filter((image) => image.naturalWidth && image.decode).map((image) => image.decode().catch(() => {}))
        );
        const settledLayout = profiles
          .map((profile) => {
            const rect = profile.getBoundingClientRect();
            return [rect.x, rect.y, rect.width, rect.height, profile.scrollHeight].join(",");
          })
          .join(";");
        if (settledLayout !== previousLayout) {
          previousLayout = settledLayout;
          lastChange = performance.now();
        }
        if (
          active &&
          revision === currentRevision &&
          document.fonts.status === "loaded" &&
          performance.now() - lastChange >= 200
        ) {
          observer.disconnect();
          onReady();
          return;
        }
      } else lastChange = performance.now();
    }
    if (active) timer = setTimeout(check, 50);
  }

  check();
  return () => {
    active = false;
    clearTimeout(timer);
    observer.disconnect();
  };
}

export function PrintReadiness({ prerequisitesReady }) {
  useLayoutEffect(() => {
    const html = document.documentElement;
    html.dataset.pdfRenderState = "loading";
    const stop = prerequisitesReady
      ? observePrintReadiness(() => {
          html.dataset.pdfRenderState = "ready";
        })
      : undefined;
    return () => {
      stop?.();
      delete html.dataset.pdfRenderState;
    };
  }, [prerequisitesReady]);

  // Keep the signal out of the template DOM so positional CSS selectors stay intact.
  return null;
}
